import { CAPABILITIES, ROLES, can, isStaff, toRole } from '../helpers/roles';
import { navItemById, pageGranted, visibleNavItems } from './navigation';

/**
 * Where "open this hub" lands someone: Settings for whoever may edit the hub,
 * Overview for everyone else — a broker sent to Settings only meets /forbidden.
 */
export const hubEntryPath = ({ userRole, capabilities }) =>
    can(userRole, CAPABILITIES.MANAGE_HUB_CONTENT, capabilities) ? '/settings' : '/overview';

// Every way of signing in ends with a push to `/`, so that is where the page
// someone was turned away from is handed back.
const RETURN_KEY = 'hub.returnTo';

const rememberReturn = (path) => {
    try {
        sessionStorage.setItem(RETURN_KEY, path);
    } catch {
        // Storage blocked: they land on `/` as before.
    }
};

const takeReturn = () => {
    try {
        const path = sessionStorage.getItem(RETURN_KEY);
        sessionStorage.removeItem(RETURN_KEY);
        return path;
    } catch {
        return null;
    }
};

/**
 * The hub URL is the door, not a brochure. Signed out, it is this hub's
 * sign-in page; signed in, it is the hub — the portal for a policyholder, the
 * Overview for staff and brokers — unless sign-in is handing back the page
 * somebody was turned away from. Sign-out and every sign-in step end on `/`,
 * so each lands here too, in the role the person has.
 */
const doorFor = (authStore) => (isStaff(authStore.userRole) ? '/overview' : '/hub');

const signInDetour = (authStore, to, router) => {
    if (!authStore.userData) {
        if (to.path === '/') {
            return { path: '/login', query: to.query };
        }
        if (!to.meta.auth) {
            return null;
        }
        rememberReturn(to.fullPath);
        return { path: '/login' };
    }

    return to.path === '/' ? (returnDetour(authStore, router) ?? doorFor(authStore)) : null;
};

/**
 * Staff are handed back the page they were turned away from only if they may
 * open it; otherwise they get the entry page, not the /forbidden it would end in.
 */
const returnDetour = (authStore, router) => {
    const path = takeReturn();
    if (!path || !isStaff(authStore.userRole)) {
        return path;
    }

    const target = router.resolve(path);
    const refused = target.matched[0] === router.resolve('/forbidden').matched[0] || (target.meta.admin && hubPageDetour(authStore, target));

    return refused ? hubEntryPath(authStore) : path;
};

/**
 * A policyholder's side of the hub is the Client Portal at /hub (XN-4552).
 * There is no hold-out: `can_access_event` is the speaker/moderator early-access
 * flag (false for every ordinary policyholder), and hubs have no start date.
 */
const isPolicyholder = (authStore) => !!authStore.userData && !isStaff(authStore.userRole);

/**
 * The portal is the policyholder's own. Its live rooms and shared recordings
 * are not marked `policyholderOnly`, because staff join sessions and open
 * hub-shared links there too.
 */
const portalDetour = (authStore, to) => {
    if (isPolicyholder(authStore)) {
        if (to.path === '/') return { path: '/hub' };
        if (to.name === 'Profile') return { path: '/hub/details' };

        return null;
    }

    if (!authStore.userData) return null;
    if (to.name === 'Profile') return { path: '/my-profile' };

    return to.meta.policyholderOnly ? { path: '/overview' } : null;
};

/**
 * `meta.admin` means "a Hub page", which now includes brokers: their data is
 * narrowed by the server on every query, so this gate is about whether they
 * belong in the Hub at all, not about what they will see once inside it.
 *
 * /forbidden rather than /: `/` is the public landing page, and dropping a
 * signed-in staff member onto marketing copy with no navigation reads as a
 * crash. /forbidden keeps the shell.
 */
const hubPageDetour = (authStore, to) => {
    if (!isStaff(authStore.userRole)) {
        return { path: '/' };
    }

    // Two ways an admin page can state who it is for, and a page uses
    // exactly one of them.
    if (to.meta.capability) {
        // The page names the power it needs. That is the whole gate,
        // for every role — including a broker the server has
        // deliberately granted that capability.
        if (!can(authStore.userRole, to.meta.capability, authStore.capabilities)) {
            return { path: '/forbidden' };
        }
    } else if (toRole(authStore.userRole) === ROLES.BROKER && !to.meta.brokerAccess) {
        // The page names no capability, so broker access is opt-in: a
        // page added later stays closed to a book-scoped viewer until
        // someone has thought about what it shows them. That is the
        // posture the server already takes, where `tenant-admin` does
        // not admit a broker at all.
        return { path: '/forbidden' };
    }

    return ungrantedPageDetour(authStore, to);
};

/** A page left out of a staff member's grant sends them to the first page they do have. */
const ungrantedPageDetour = ({ userRole, capabilities, pages }, to) => {
    if (pageGranted(navItemById(to.meta.navItem), userRole, pages)) {
        return null;
    }

    const first = visibleNavItems(userRole, capabilities, pages).find((item) => item.to);

    return { path: first ? first.to : '/forbidden' };
};

/**
 * The Hub's navigation guard.
 *
 * Extracted from index.js so it can be tested against a real router: index.js
 * has a top-level `await` and mounts the app as a side effect, so importing it
 * in a test is not an option — and this is the half of "hiding a link is not a
 * permission" that a test has to prove.
 *
 * It returns a location rather than calling `router.push()` and cancelling,
 * which is the shape vue-router documents and the one that cannot race.
 *
 * This is a UX boundary, not a security one. Every endpoint behind these pages
 * is already gated server-side, and `can()` prefers the capability list the
 * server sent — so a person who bypassed the router entirely still gets
 * refused by the API. What this stops is someone landing on a page that will
 * only fail underneath them.
 *
 * @param {import('pinia').Store} authStore
 * @param {{historyChanges: Array, changeRouteDialog: Object}} store window.store
 * @param {import('vue-router').Router} router
 */
export function createHubGuard(authStore, store, router) {
    return (to) => {
        const detour = signInDetour(authStore, to, router);
        if (detour) {
            return detour;
        }

        // A link into a session's room — a reminder email, an old bookmark —
        // reads /hub(/sessions)?session=. Staff run sessions in the Agency Hub,
        // never in the policyholder portal: they open it over Sessions there.
        if (authStore.userData && isStaff(authStore.userRole)
            && (to.path === '/hub' || to.path === '/hub/sessions') && to.query.session) {
            return { path: '/sessions', query: { room: to.query.session } };
        }

        // For a policyholder, the old /hub page's ?session= opens the room on
        // the portal's Sessions.
        if (to.path === '/hub' && to.query.session) {
            return { path: '/hub/sessions', query: to.query };
        }

        const portal = portalDetour(authStore, to);
        if (portal) {
            return portal;
        }

        if (authStore.userData && to.meta.admin) {
            const admin = hubPageDetour(authStore, to);
            if (admin) {
                return admin;
            }
        }

        if (store.historyChanges.length > 0) {
            store.changeRouteDialog = {
                show: true,
                toRoute: to,
                external: false,
            };
        }

        return store.historyChanges.length === 0;
    };
}
